import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { 
  Package, Thermometer, MapPin, Clock, CheckCircle, 
  AlertTriangle, Truck, ArrowRight, Home, RefreshCw
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PublicTracking = () => {
  const { sscc } = useParams();
  const [shipment, setShipment] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchShipment();
    // Auto-refresh every 30 seconds
    const interval = setInterval(fetchShipment, 30000);
    return () => clearInterval(interval);
  }, [sscc]);

  const fetchShipment = async () => {
    try {
      const res = await axios.get(`${API}/track/${sscc}`);
      setShipment(res.data);
      setError(null);
    } catch (err) {
      setError(err.response?.data?.detail || 'Shipment not found');
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'created': return 'bg-gray-100 text-gray-700';
      case 'activated': return 'bg-blue-100 text-blue-700';
      case 'in_transit': return 'bg-indigo-100 text-indigo-700';
      case 'geofence_entered': return 'bg-yellow-100 text-yellow-700';
      case 'completed': return 'bg-green-100 text-green-700';
      default: return 'bg-gray-100 text-gray-700';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'in_transit': return <Truck className="w-6 h-6" />;
      case 'geofence_entered': return <MapPin className="w-6 h-6" />;
      case 'completed': return <CheckCircle className="w-6 h-6" />;
      default: return <Package className="w-6 h-6" />;
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    try {
      return new Date(dateStr).toLocaleString();
    } catch {
      return dateStr;
    }
  };

  const isTemperatureInRange = () => {
    if (!shipment?.temperature?.current) return true;
    const { current, expected_min, expected_max } = shipment.temperature;
    return current >= expected_min && current <= expected_max;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50 flex items-center justify-center">
        <div className="text-center">
          <RefreshCw className="w-12 h-12 mx-auto text-blue-500 animate-spin mb-4" />
          <p className="text-gray-600">Loading shipment details...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 via-gray-100 to-gray-50 flex items-center justify-center p-4">
        <Card className="max-w-md w-full bg-white/80 backdrop-blur-md">
          <CardContent className="p-8 text-center">
            <AlertTriangle className="w-16 h-16 mx-auto text-orange-500 mb-4" />
            <h2 className="text-xl font-bold text-gray-900 mb-2">Shipment Not Found</h2>
            <p className="text-gray-600 mb-6">{error}</p>
            <p className="text-sm text-gray-500 mb-6">SSCC: {sscc}</p>
            <Link to="/">
              <Button className="bg-blue-600 hover:bg-blue-700">
                <Home className="w-4 h-4 mr-2" />
                Go to Homepage
              </Button>
            </Link>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50 p-4 md:p-8">
      {/* Header */}
      <div className="max-w-2xl mx-auto mb-6">
        <Link to="/" className="inline-flex items-center text-blue-600 hover:text-blue-700 mb-4">
          <Home className="w-4 h-4 mr-2" />
          SmartPallet
        </Link>
        <h1 className="text-2xl md:text-3xl font-bold text-gray-900">Shipment Tracking</h1>
        <p className="text-gray-600 mt-1">Real-time tracking for your shipment</p>
      </div>

      {/* Main Status Card */}
      <div className="max-w-2xl mx-auto mb-6">
        <Card className="bg-white/80 backdrop-blur-md border-white/50 shadow-xl overflow-hidden">
          <div className={`h-2 ${shipment?.status === 'completed' ? 'bg-green-500' : 'bg-blue-500'}`} />
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className={`w-14 h-14 rounded-xl flex items-center justify-center ${
                  shipment?.status === 'completed' ? 'bg-green-100 text-green-600' : 'bg-blue-100 text-blue-600'
                }`}>
                  {getStatusIcon(shipment?.status)}
                </div>
                <div>
                  <CardTitle className="text-lg">SSCC: {shipment?.sscc?.slice(-12)}</CardTitle>
                  <Badge className={`mt-1 ${getStatusColor(shipment?.status)}`}>
                    {shipment?.status?.replace('_', ' ')?.toUpperCase()}
                  </Badge>
                </div>
              </div>
              <Button variant="outline" size="sm" onClick={fetchShipment} data-testid="refresh-tracking-btn">
                <RefreshCw className="w-4 h-4" />
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            {/* Route */}
            <div className="flex items-center gap-3 py-4 border-b border-gray-100">
              <div className="flex-1">
                <div className="text-sm text-gray-500">From</div>
                <div className="font-semibold text-gray-900">{shipment?.origin || 'N/A'}</div>
              </div>
              <ArrowRight className="w-5 h-5 text-gray-400" />
              <div className="flex-1 text-right">
                <div className="text-sm text-gray-500">To</div>
                <div className="font-semibold text-gray-900">{shipment?.receiver_name || shipment?.destination || 'N/A'}</div>
              </div>
            </div>

            {/* Temperature */}
            <div className="py-4 border-b border-gray-100">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Thermometer className={`w-5 h-5 ${isTemperatureInRange() ? 'text-green-500' : 'text-red-500'}`} />
                  <span className="text-sm text-gray-500">Temperature</span>
                </div>
                {!isTemperatureInRange() && (
                  <Badge className="bg-red-100 text-red-700">
                    <AlertTriangle className="w-3 h-3 mr-1" />
                    Out of Range
                  </Badge>
                )}
              </div>
              <div className="mt-2 flex items-baseline gap-2">
                <span className={`text-3xl font-bold ${isTemperatureInRange() ? 'text-gray-900' : 'text-red-600'}`}>
                  {shipment?.temperature?.current !== null ? `${shipment?.temperature?.current?.toFixed(1)}°C` : '--'}
                </span>
                <span className="text-sm text-gray-500">
                  (Required: {shipment?.temperature?.expected_min}°C to {shipment?.temperature?.expected_max}°C)
                </span>
              </div>
              {shipment?.temperature?.last_reading && (
                <div className="text-xs text-gray-400 mt-1">
                  Last reading: {formatDate(shipment.temperature.last_reading)}
                </div>
              )}
            </div>

            {/* Location */}
            {shipment?.current_location && (
              <div className="py-4 border-b border-gray-100">
                <div className="flex items-center gap-2 mb-2">
                  <MapPin className="w-5 h-5 text-blue-500" />
                  <span className="text-sm text-gray-500">Current Location</span>
                </div>
                <div className="text-sm font-mono text-gray-700">
                  {shipment.current_location.latitude?.toFixed(4)}, {shipment.current_location.longitude?.toFixed(4)}
                </div>
                {shipment.current_location.last_update && (
                  <div className="text-xs text-gray-400 mt-1">
                    Updated: {formatDate(shipment.current_location.last_update)}
                  </div>
                )}
              </div>
            )}

            {/* ETA */}
            {shipment?.eta && (
              <div className="py-4 border-b border-gray-100">
                <div className="flex items-center gap-2">
                  <Clock className="w-5 h-5 text-indigo-500" />
                  <span className="text-sm text-gray-500">Estimated Arrival</span>
                </div>
                <div className="text-lg font-semibold text-gray-900 mt-1">
                  {formatDate(shipment.eta)}
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Timeline Card */}
      <div className="max-w-2xl mx-auto">
        <Card className="bg-white/80 backdrop-blur-md border-white/50 shadow-lg">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Clock className="w-5 h-5 text-gray-500" />
              Shipment Timeline
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {/* Created */}
              <div className="flex items-start gap-3">
                <div className={`w-3 h-3 rounded-full mt-1.5 ${shipment?.timeline?.created_at ? 'bg-green-500' : 'bg-gray-300'}`} />
                <div className="flex-1">
                  <div className="font-medium text-gray-900">Shipment Created</div>
                  <div className="text-sm text-gray-500">{formatDate(shipment?.timeline?.created_at)}</div>
                </div>
              </div>

              {/* Activated */}
              <div className="flex items-start gap-3">
                <div className={`w-3 h-3 rounded-full mt-1.5 ${shipment?.timeline?.activated_at ? 'bg-green-500' : 'bg-gray-300'}`} />
                <div className="flex-1">
                  <div className="font-medium text-gray-900">In Transit</div>
                  <div className="text-sm text-gray-500">{formatDate(shipment?.timeline?.activated_at)}</div>
                </div>
              </div>

              {/* Geofence Entered */}
              <div className="flex items-start gap-3">
                <div className={`w-3 h-3 rounded-full mt-1.5 ${shipment?.timeline?.geofence_entered_at ? 'bg-green-500' : 'bg-gray-300'}`} />
                <div className="flex-1">
                  <div className="font-medium text-gray-900">Arrived at Destination</div>
                  <div className="text-sm text-gray-500">
                    {shipment?.timeline?.geofence_entered_at ? formatDate(shipment.timeline.geofence_entered_at) : 'Pending'}
                  </div>
                </div>
              </div>

              {/* Completed */}
              <div className="flex items-start gap-3">
                <div className={`w-3 h-3 rounded-full mt-1.5 ${shipment?.timeline?.completed_at ? 'bg-green-500' : 'bg-gray-300'}`} />
                <div className="flex-1">
                  <div className="font-medium text-gray-900">Delivered</div>
                  <div className="text-sm text-gray-500">
                    {shipment?.timeline?.completed_at ? formatDate(shipment.timeline.completed_at) : 'Pending'}
                  </div>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Footer */}
      <div className="max-w-2xl mx-auto mt-8 text-center text-sm text-gray-500">
        <p>Powered by SmartPallet IoT Platform</p>
        <p className="mt-1">Real-time temperature monitoring and tracking</p>
      </div>
    </div>
  );
};

export default PublicTracking;
